import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  GraduationCap,
  CheckCircle,
  Trophy,
  Users,
  UserPlus,
  ClipboardList,
  Clock,
  Play,
  TrendingUp,
  TrendingDown,
  Loader,
  RefreshCw,
} from 'lucide-react';
import apiService from '../../services/api';
import { useApiData } from '../../hooks/useApi';
import useTokenRefresh from '../../hooks/useTokenRefresh';
import ReferralPartnerMain from '../referral/ReferralPartnerMain';

const ResponsiveDashboard = ({ user, selectedCountry = 'all' }) => {
  useTokenRefresh(); // Auto-refresh tokens
  const [timeRange, setTimeRange] = useState('7d');
  const [refreshing, setRefreshing] = useState(false);

  const {
    data: dashboardData,
    loading: statsLoading,
    refetch,
  } = useApiData(() => {
    const countryId = selectedCountry === 'all' ? null : selectedCountry;
    return apiService.getDashboardStats(countryId);
  }, [timeRange, selectedCountry]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  };

  const stats = useMemo(() => {
    const statsData = [
      {
        icon: Mail,
        label: 'Total Enquiries',
        value: dashboardData?.totalEnquiries || '0',
        change: dashboardData?.enquiriesChange || '0%',
        changeType: dashboardData?.enquiriesChange?.startsWith('+') ? 'positive' : 'negative',
        color: 'var(--primary)',
        description: 'All admission enquiries',
      },
      {
        icon: Clock,
        label: 'Pending Enquiries',
        value: dashboardData?.pendingEnquiries || '0',
        change: dashboardData?.pendingChange || '0%',
        changeType: dashboardData?.pendingChange?.startsWith('+') ? 'negative' : 'positive',
        color: 'var(--warning)',
        description: 'Awaiting response',
      },
      {
        icon: Play,
        label: 'In Progress',
        value: dashboardData?.inProgressEnquiries || '0',
        change: dashboardData?.inProgressChange || '0%',
        changeType: dashboardData?.inProgressChange?.startsWith('+') ? 'positive' : 'negative',
        color: 'var(--info)',
        description: 'Currently processing',
      },
      {
        icon: CheckCircle,
        label: 'Completed',
        value: dashboardData?.completedEnquiries || '0',
        change: dashboardData?.completedChange || '0%',
        changeType: dashboardData?.completedChange?.startsWith('+') ? 'positive' : 'negative',
        color: 'var(--success)',
        description: 'Successfully processed',
      },
      {
        icon: Trophy,
        label: 'Success Rate',
        value: dashboardData?.successRate || '0%',
        change: dashboardData?.successRateChange || '0%',
        changeType: dashboardData?.successRateChange?.startsWith('+') ? 'positive' : 'negative',
        color: 'var(--accent)',
        description: 'Conversion percentage',
      },
      {
        icon: Users,
        label: 'Business Partners',
        value: dashboardData?.totalPartners || '0',
        change: dashboardData?.partnersChange || '0%',
        changeType: dashboardData?.partnersChange?.startsWith('+') ? 'positive' : 'negative',
        color: 'var(--secondary)',
        description: 'Active partners',
      },
      {
        icon: UserPlus,
        label: 'Academic Counsellor',
        value: dashboardData?.totalTeamLeads || '0',
        change: dashboardData?.teamLeadsChange || '0%',
        changeType: dashboardData?.teamLeadsChange?.startsWith('+') ? 'positive' : 'negative',
        color: 'var(--primary)',
        description: 'Team members',
      },
      {
        icon: ClipboardList,
        label: 'Active Tasks',
        value: dashboardData?.activeTasks || '0',
        change: dashboardData?.tasksChange || '0%',
        changeType: dashboardData?.tasksChange?.startsWith('+') ? 'positive' : 'negative',
        color: '#9c27b0',
        description: 'Ongoing tasks',
      },
    ];
    return statsData;
  }, [dashboardData]);

  // Show referral partner dashboard if user is a referral partner
  if (user?.role === 'referral_partner') {
    return <ReferralPartnerMain user={user} />;
  }

  if (statsLoading) {
    return (
      <div className='responsive-card'>
        <div className='responsive-loading'>
          <div className='responsive-spinner' />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      {/* Dashboard Header */}
      <motion.div
        className='responsive-card'
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className='responsive-card-header'>
          <div>
            <h2
              style={{
                fontSize: 'var(--text-2xl)',
                fontWeight: '700',
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: 'var(--space-2)',
              }}
            >
              Welcome back, {user?.name}!
            </h2>
            <p style={{ color: 'var(--gray-600)', fontSize: 'var(--text-sm)' }}>
              Here's what's happening with your admin panel today.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            {/* Time Range Selector */}
            <select
              className='responsive-form-input responsive-form-select'
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              style={{ minWidth: '120px' }}
            >
              <option value='1d'>Last 24h</option>
              <option value='7d'>Last 7 days</option>
              <option value='30d'>Last 30 days</option>
              <option value='90d'>Last 90 days</option>
            </select>

            {/* Refresh Button */}
            <motion.button
              className='responsive-btn responsive-btn-secondary responsive-btn-sm'
              onClick={handleRefresh}
              disabled={refreshing}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label='Refresh dashboard'
            >
              <motion.div
                animate={{ rotate: refreshing ? 360 : 0 }}
                transition={{ duration: 1, repeat: refreshing ? Infinity : 0, ease: 'linear' }}
              >
                <RefreshCw size={16} />
              </motion.div>
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className='responsive-grid cols-4'>
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          const isPositive = stat.changeType === 'positive';

          return (
            <motion.div
              key={index}
              className='responsive-card'
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: index * 0.1, duration: 0.6, type: 'spring' }}
              whileHover={{ y: -4, scale: 1.02 }}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
                {/* Icon */}
                <motion.div
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: 'var(--radius-xl)',
                    background: `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}25 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  whileHover={{ rotate: 10, scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <IconComponent size={24} style={{ color: stat.color }} />
                </motion.div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <motion.h3
                    style={{
                      fontSize: 'var(--text-2xl)',
                      fontWeight: '800',
                      color: 'var(--gray-900)',
                      marginBottom: 'var(--space-1)',
                      lineHeight: 1,
                    }}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 + index * 0.1, type: 'spring' }}
                  >
                    {stat.value}
                  </motion.h3>

                  <p
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: '600',
                      color: 'var(--gray-700)',
                      marginBottom: 'var(--space-2)',
                    }}
                  >
                    {stat.label}
                  </p>

                  <p
                    style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--gray-500)',
                      marginBottom: 'var(--space-2)',
                    }}
                  >
                    {stat.description}
                  </p>

                  {/* Change Indicator */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5 + index * 0.1, type: 'spring' }}
                    >
                      {isPositive ? (
                        <TrendingUp size={14} style={{ color: 'var(--success)' }} />
                      ) : (
                        <TrendingDown size={14} style={{ color: 'var(--danger)' }} />
                      )}
                    </motion.div>
                    <span
                      style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: '600',
                        color: isPositive ? 'var(--success)' : 'var(--danger)',
                      }}
                    >
                      {stat.change}
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--gray-400)',
                        fontWeight: '500',
                        opacity: 0.8,
                      }}
                    >
                      vs last period
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default ResponsiveDashboard;
